import { getAdministrativeRegion } from '@yembo/yemblocks-core/dist/_util';
import { warn as logRocketWarnLog } from 'logrocket';
import { isLogRocketEnabled } from '../../analytics/logRocket';
import { getJobByKeyApi } from '../yembo/job';
import { putPlaceApi } from '../yembo/place';

// biome-ignore lint/suspicious/noConsole: console.warn used as fallback when LogRocket is disabled
const warn = isLogRocketEnabled ? logRocketWarnLog : console.warn;

interface PlaceApiAction {
  type: string;
  data: Record<string, unknown>;
  skipDispatch?: boolean;
}

interface PlaceData {
  streetAddress?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  jobKey?: string;
  [key: string]: unknown;
}

export const putPlaceApiLatLng = async (action: PlaceApiAction): Promise<void> => {
  try {
    //if streetAddress, city, state, or postalCode are being sent to API, fetch the coords
    if (shouldFetchCoords(action)) {
      const { data: actionData, skipDispatch } = action;

      const { latitude, longitude } = (await getPlaceCoordinates(actionData as PlaceData)) ?? {};

      const data = {
        ...actionData,
        latitude: latitude ?? null,
        longitude: longitude ?? null,
      };

      await putPlaceApi({ data, skipDispatch });

      if (!skipDispatch) {
        await getJobByKeyApi({ data: { key: actionData.jobKey as string } });
      }
    }
  } catch (error) {
    warn({ error });
  }
};

const shouldFetchCoords = (action: PlaceApiAction): boolean => {
  if (action.type.includes('geocoord')) return false;

  const locationKeys = ['streetAddress', 'city', 'state', 'postalCode'];
  const dataKeys = Object.keys(action.data);
  return locationKeys.some((locationKey) => dataKeys.includes(locationKey));
};

const getPlaceCoordinates = async (placeData: PlaceData): Promise<{ latitude: number; longitude: number } | null> => {
  const result = await callGeocodeApi(placeData);

  if (!result) return null;

  //types is an array reflecting the granular detail of the lookup
  //only use the lat/lng if the result is more specific than country or state
  //https://developers.google.com/maps/documentation/geocoding

  const geoGranularity = result.types[0];
  const nonGranularAreas = ['country', 'administrative_area_level_1'];
  const isResultGranular = !nonGranularAreas.includes(geoGranularity);

  if (!isResultGranular) return null;

  const { location } = result.geometry;
  return {
    latitude:
      typeof location.lat === 'function' ? (location.lat as () => number)() : (location.lat as unknown as number),
    longitude:
      typeof location.lng === 'function' ? (location.lng as () => number)() : (location.lng as unknown as number),
  };
};

// The Maps script loads with `loading=async`, which guarantees only the
// libraries named in its URL, so ask for the geocoding one rather than
// assuming `google.maps.Geocoder` is already there.
const getGeocoder = async () => {
  const { Geocoder } = (await google.maps.importLibrary('geocoding')) as google.maps.GeocodingLibrary;
  return new Geocoder();
};

const callGeocodeApi = async (outgoing: PlaceData): Promise<google.maps.GeocoderResult | null> => {
  /*
   * Address format: 123 Main Street, Anytown, CA 92000, US
   * https://developers.google.com/maps/documentation/geocoding/start#geocoding-request-and-response-latitudelongitude-lookup
   */

  const { streetAddress, city, postalCode, country } = outgoing;
  const administrativeRegion = getAdministrativeRegion(outgoing);
  const addressParts = [streetAddress, city, administrativeRegion, postalCode, country].filter(Boolean);
  const address = addressParts.join(', ');
  const componentRestrictions = {
    country: country || undefined,
    postalCode: postalCode || undefined,
    administrativeArea: administrativeRegion || undefined,
  };

  try {
    const geocoder = await getGeocoder();
    const { results } = await geocoder.geocode({
      address,
      componentRestrictions,
    });

    return results[0] ?? null;
  } catch (err) {
    if (typeof err === 'object') {
      warn(
        (err as Error).message || 'Failed to fetch data from Geocode API: ',
        address,
        JSON.stringify(componentRestrictions)
      );
    }

    return null;
  }
};

interface LocationData {
  country?: string;
  [key: string]: unknown;
}

export const googleValidatePostalCode = async (postalCode: string, location: LocationData): Promise<boolean> => {
  //soft validation - only will return false on a successful API call that returns no results (i.e. an unrecognized postal code)

  const { country } = location;
  const administrativeRegion = getAdministrativeRegion(location as unknown as Record<string, unknown>);

  if (!postalCode || !country) return true;
  if (country === 'US' && postalCode.replace(/\s+/g, '').length !== 5) return false;

  const addressComponents = {
    country,
    postalCode,
    administrativeArea: administrativeRegion || undefined,
  };

  try {
    const geocoder = await getGeocoder();
    const result = await geocoder.geocode({
      componentRestrictions: addressComponents,
    });

    return !!result.results.length;
  } catch (err) {
    if (typeof err === 'object') {
      warn(
        (err as Error).message ?? 'No result was found for this validate postal code request. Address components:',
        JSON.stringify(addressComponents)
      );
    }

    return false;
  }
};
